"use client"

import { Fragment, useState, useRef } from "react"
import {
  TextField,
  Typography,
  Paper,
  InputAdornment,
  Popper,
  ClickAwayListener,
  List,
  ListItem,
  ListItemText,
  Box,
  Checkbox,
  Chip,
  Stack,
} from "@mui/material"
import SearchIcon from "@mui/icons-material/Search"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"

function MultiSelectField(props) {
  const { label, disabled, value = [], onSelect, name, error, options = [], register } = props

  const [open, setOpen] = useState(false)
  const [searchValue, setSearchValue] = useState("")
  const anchorRef = useRef(null)

  const filteredOptions = options.filter((option) =>
    (option.label || option.name || "").toLowerCase().includes(searchValue.toLowerCase()),
  )

  const handleToggle = (option) => {
    let newValue = []
    if (option.id === "all") {
      if (value.length === options.length) {
        newValue = [] // deselect all
      } else {
        newValue = [...options] // select all
      }
    } else {
      if (value.find((v) => v.id === option.id)) {
        newValue = value.filter((v) => v.id !== option.id)
      } else {
        newValue = [...value, option]
      }
    }
    onSelect(newValue)
  }

  const handleDelete = (chipToDelete) => {
    const newValue = value.filter((v) => v.id !== chipToDelete.id)
    onSelect(newValue)
  }

  const handleMainFieldClick = () => {
    if (!disabled) {
      setOpen(!open)
    }
  }

  const handleClickAway = () => {
    setOpen(false)
    setSearchValue("")
  }

  return (
    <Fragment>
      <ClickAwayListener onClickAway={handleClickAway}>
        <Box sx={{ position: "relative", width: "100%" }}>
          {label && <label style={{ color: "#a3abb2", fontWeight: 600, fontFamily: "Maven Pro, sans-serif" }}>{label}</label>}

          {/* Hidden field for RHF */}
          <input
            type="hidden"
            name={name}
            value={value.map((v) => v.id).join(",")}
            {...register}
          />

          <TextField
            ref={anchorRef}
            value=""
            placeholder={label || "Select States"}
            error={!!error}
            disabled={disabled}
            onClick={handleMainFieldClick}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <ExpandMoreIcon
                    sx={{
                      color: "#8A9BA8",
                      cursor: disabled ? "default" : "pointer",
                      transform: open ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.2s",
                    }}
                  />
                </InputAdornment>
              ),
            }}
            sx={{
              width: "100%",
              mt: 1,
              cursor: disabled ? "default" : "pointer",
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#273346",
                height: "47px",
                borderRadius: "8px",
                border: "1px solid #3A4A5C",
                color: "#E0E6ED",
              },
              "& .MuiInputBase-input": {
                color: "#E0E6ED",
                fontSize: "14px",
              },
            }}
          />

          {/* Chips */}
          {value.length > 0 && (
            <Stack
              direction="row"
              flexWrap="wrap"
              gap={1} // ✅ works in both directions
              sx={{ mt: 1 }}
            >
              {value.map((v) => (
                <Chip
                  key={v.id}
                  label={v.label || v.name}
                  onDelete={() => handleDelete(v)}
                  sx={{
                    backgroundColor: "#00BCD4",
                    color: "#fff",
                    "& .MuiChip-deleteIcon": {
                      color: "#fff",
                    },
                  }}
                />
              ))}
            </Stack>
          )}

          {/* Dropdown */}
          <Popper
            open={open}
            anchorEl={anchorRef.current}
            placement="bottom-start"
            sx={{ zIndex: 1300, width: anchorRef.current?.offsetWidth }}
          >
            <Paper
              sx={{
                backgroundColor: "#273346",
                border: "1px solid #3A4A5C",
                borderRadius: "8px",
                marginTop: "4px",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
                width: "100%",
              }}
            >
              {/* Search */}
              <Box sx={{ p: 2, borderBottom: "1px solid #3A4A5C" }}>
                <TextField
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.id)}
                  placeholder="Search"
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: "#8A9BA8", fontSize: "18px" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#3A4A5C",
                      borderRadius: "6px",
                      color: "#E0E6ED",
                    },
                  }}
                />
              </Box>

              <List sx={{ padding: 0, maxHeight: "200px", overflow: "auto" }}>
                {/* Select All */}
                <ListItem
                  onClick={() => handleToggle({ id: "all", label: "Select All" })}
                  sx={{
                    cursor: "pointer",
                    color: "#E0E6ED",
                    fontSize: "14px",
                    padding: "8px 16px",
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={value.length === options.length}
                    sx={{ color: "#8A9BA8", "&.Mui-checked": { color: "#00BCD4" } }}
                  />
                  <ListItemText primary="Select All" />
                </ListItem>

                {/* Other States */}
                {filteredOptions.length > 0 ? (
                  filteredOptions.map((option, index) => (
                    <ListItem
                      key={option.id || index}
                      onClick={() => handleToggle(option)}
                      sx={{
                        cursor: "pointer",
                        color: "#E0E6ED",
                        fontSize: "14px",
                        padding: "8px 16px",
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={!!value.find((v) => v.id === option.id)}
                        sx={{ color: "#8A9BA8", "&.Mui-checked": { color: "#00BCD4" } }}
                      />
                      <ListItemText
                        primary={option.label || option.name}
                        sx={{ "& .MuiListItemText-primary": { fontSize: "14px", color: "#E0E6ED" } }}
                      />
                    </ListItem>
                  ))
                ) : (
                  <ListItem sx={{ color: "#8A9BA8", fontSize: "14px", padding: "12px 16px" }}>
                    <ListItemText primary="No options found" />
                  </ListItem>
                )}
              </List>
            </Paper>
          </Popper>
        </Box>
      </ClickAwayListener>

      {error && (
        <Typography variant="caption" color="error" sx={{ textAlign: "left", mt: 1 }}>
          {error}
        </Typography>
      )}
    </Fragment>
  )
}

export default MultiSelectField
